import { useMemo, useRef } from 'react'
import { createMessageApiService, type MessageApiService } from '../services/api-service'
import { useMessageComponents } from '../components/message-components-provider'

/**
 * Custom hook to get a configured API service instance
 * This ensures consistent configuration across components
 *
 * The instance is memoized on the endpoint **alone**, and the token is read through a ref at call
 * time. Keying it on the token getter as well made the instance change on every render for any
 * consumer passing an inline `userToken` function — and effects all over this package list the
 * service in their dependency array, so that re-ran them: visible as duplicate `/user` and
 * `/related-entities` requests every time the panel opened. The ref keeps token refreshes working
 * without paying for that.
 */
export function useMessageApi(): MessageApiService {
  const { config } = useMessageComponents()

  const tokenRef = useRef(config.userToken)
  tokenRef.current = config.userToken

  return useMemo(() => createMessageApiService({
    apiEndpoint: config.apiEndpoint,
    userToken: () => {
      const userToken = tokenRef.current
      return typeof userToken === 'function' ? userToken() : (userToken ?? '')
    }
  }), [config.apiEndpoint])
}
